import { ReactNode, useRef, useState } from 'react';

import { DataType, PersonOrgOnboardingUpdateRequest, PersonOrgType } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useInvitePerson } from 'views/person/person-queries';

import { Field, Segmented, SelectField, TextField } from './fields';
import {
    arePercentsComplete,
    CLIENT_LABEL,
    data,
    ENTITY_TYPES,
    FAMILY_LABEL,
    isValidEmail,
    PersonKind,
    RelatedPerson,
    roleLabelOf,
    toEntityLinks,
    toRoleChoice,
} from './onboarding-model';
import { useCreateHouseholdPersonOrg, useSaveOnboardingProfile } from './onboarding-tab-queries';
import { ProfessionalRoleFields } from './ProfessionalRoleFields';
import { RelatedPeopleRows, withNewRow } from './RelatedPeopleRows';
import { OnboardingHousehold } from './useOnboardingHousehold';

const PERSON_TYPES = [
    { value: PersonKind.CLIENT, label: 'Client' },
    { value: PersonKind.FAMILY, label: 'Family' },
    { value: PersonKind.PROFESSIONAL, label: 'Trusted Professional' },
];

/** The household role label a new person is created with; a professional's is their role. */
const ROLE_LABELS: Partial<Record<PersonKind, string>> = {
    [PersonKind.CLIENT]: CLIENT_LABEL,
    [PersonKind.FAMILY]: FAMILY_LABEL,
};

/** The onboarding fields of a person just created: all empty but these. */
const newPersonRequest = (fields: Partial<PersonOrgOnboardingUpdateRequest>): PersonOrgOnboardingUpdateRequest => ({
    archived: false,
    ...fields,
});

export const AddPersonDialog = ({
    householdGUID,
    tenantGUID,
    initialKind,
    onClose,
    onCreated,
}: {
    householdGUID: string;
    /** The household's tenant; inviting waits for it. */
    tenantGUID?: string;
    initialKind: PersonKind;
    onClose: () => void;
    onCreated: (personOrgGUID: string) => void;
}) => {
    const [kind, setKind] = useState(initialKind);
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState<string>();
    const [role, setRole] = useState(() => toRoleChoice());
    const roleLabel = kind === PersonKind.PROFESSIONAL ? roleLabelOf(role) : ROLE_LABELS[kind];
    const create = useCreateHouseholdPersonOrg(householdGUID);
    const save = useSaveOnboardingProfile(householdGUID);
    const invite = useInvitePerson(tenantGUID);
    const pending = create.isPending || save.isPending || invite.isPending;
    const canSave = !!firstName.trim() && !!lastName.trim() && !!roleLabel && !pending;

    // Kept across retries: a failed later step reruns on the person already created instead of adding another
    const created = useRef<string>();

    const submit = async (andInvite: boolean) => {
        const normalizedEmail = email.trim().toLowerCase();
        if ((normalizedEmail && !isValidEmail(normalizedEmail)) || (andInvite && !normalizedEmail)) {
            setEmailError('Enter a valid email address');
            return;
        }
        const personOrgGUID =
            created.current ?? (await create.mutateAsync({ personOrgType: PersonOrgType.PERSON, roleLabel }));
        created.current = personOrgGUID;
        await save.mutateAsync({
            personOrgGUID,
            request: {
                personOrg: newPersonRequest({
                    firstName: data(personOrgGUID, DataType.PERSON_ORG_INFORMATION_FIRST_NAME, firstName),
                    middleName: data(personOrgGUID, DataType.PERSON_ORG_INFORMATION_MIDDLE_NAME, middleName),
                    lastName: data(personOrgGUID, DataType.PERSON_ORG_INFORMATION_LAST_NAME, lastName),
                    primaryEmail: data(personOrgGUID, DataType.PERSON_ORG_EMAIL_ADDRESS, normalizedEmail),
                }),
            },
        });
        if (andInvite) await invite.mutateAsync(personOrgGUID);
        onCreated(personOrgGUID);
        onClose();
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-[520px]">
                <DialogHeader>
                    <DialogTitle className="font-heading text-[22px] font-normal">Add person</DialogTitle>
                </DialogHeader>
                <Field label="Type">
                    {() => (
                        <Segmented
                            aria-label="Type"
                            value={kind}
                            onChange={setKind}
                            options={PERSON_TYPES}
                            // The type is the household role set when the person was created
                            disabled={!!created.current}
                        />
                    )}
                </Field>
                {kind === PersonKind.PROFESSIONAL && (
                    <div className="grid grid-cols-2 gap-4">
                        {/* Set when the person is created, like the type */}
                        <ProfessionalRoleFields value={role} onChange={setRole} disabled={!!created.current} />
                    </div>
                )}
                <div className="grid grid-cols-3 gap-4">
                    <TextField label="First name" value={firstName} onChange={setFirstName} />
                    <TextField label="Middle name" value={middleName} onChange={setMiddleName} />
                    <TextField label="Last name" value={lastName} onChange={setLastName} />
                </div>
                <TextField
                    label="Email"
                    value={email}
                    onChange={(value) => {
                        setEmail(value);
                        setEmailError(undefined);
                    }}
                    error={emailError}
                    helper={kind === PersonKind.CLIENT ? 'Login username for the Client Portal' : undefined}
                />
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    {kind === PersonKind.CLIENT && (
                        <Button
                            disabled={!canSave || !email.trim() || !tenantGUID}
                            onClick={() => submit(true).catch(() => {})}
                        >
                            Save and invite to Client Portal
                        </Button>
                    )}
                    <Button
                        variant={kind === PersonKind.CLIENT ? 'outline' : 'default'}
                        disabled={!canSave}
                        onClick={() => submit(false).catch(() => {})}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const AddEntityDialog = ({
    householdGUID,
    household,
    onClose,
    onCreated,
}: {
    householdGUID: string;
    household: OnboardingHousehold;
    onClose: () => void;
    onCreated?: (personOrgGUID: string) => void;
}) => {
    const [type, setType] = useState(ENTITY_TYPES[0].value);
    const [legalName, setLegalName] = useState('');
    const entityType = ENTITY_TYPES.find((candidate) => candidate.value === type)!;
    const [related, setRelated] = useState<RelatedPerson[]>(() => withNewRow([], entityType.type));
    const create = useCreateHouseholdPersonOrg(householdGUID);
    const save = useSaveOnboardingProfile(householdGUID);
    const pending = create.isPending || save.isPending;
    const rows = related.filter((row) => row.personOrgGUID);
    const canSave = !!legalName.trim() && rows.every((row) => row.role) && arePercentsComplete(rows) && !pending;

    /** A trust and a business take different roles, so the rows start again when the type crosses over. */
    const changeType = (next: string) => {
        const nextType = ENTITY_TYPES.find((candidate) => candidate.value === next)!;
        if (nextType.type !== entityType.type) setRelated(withNewRow([], nextType.type));
        setType(next);
    };

    // Kept across retries: a failed save reruns on the entity already created instead of adding another
    const created = useRef<string>();

    const submit = async () => {
        const personOrgGUID =
            created.current ??
            (await create.mutateAsync({ personOrgType: entityType.type, personOrgSubtype: entityType.subtype }));
        created.current = personOrgGUID;
        await save.mutateAsync({
            personOrgGUID,
            request: {
                personOrg: newPersonRequest({
                    personOrgSubtype: entityType.subtype,
                    orgName: data(personOrgGUID, DataType.PERSON_ORG_INFORMATION_ORGANIZATION_NAME, legalName),
                }),
                // Name and people in one save, which validates and sets the whole link set at once
                personOrgLinks: toEntityLinks(personOrgGUID, rows, []),
            },
        });
        onCreated?.(personOrgGUID);
        onClose();
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-[560px]">
                <DialogHeader>
                    <DialogTitle className="font-heading text-[22px] font-normal">Add entity</DialogTitle>
                    <DialogDescription className="sr-only">A trust or business on the household</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-4">
                    <SelectField
                        label="Type"
                        value={type}
                        onChange={changeType}
                        options={ENTITY_TYPES}
                        // A trust or a business is fixed once created
                        disabled={!!created.current}
                    />
                    <TextField
                        label="Legal name"
                        value={legalName}
                        onChange={setLegalName}
                        placeholder="e.g. Hartwell Family Revocable Trust"
                    />
                </div>
                <div className="flex flex-col gap-2.5">
                    <span className="text-cocoa-400 text-xs font-medium tracking-[0.1em] uppercase">
                        Related people
                    </span>
                    <RelatedPeopleRows
                        entityType={entityType.type}
                        rows={related}
                        onChange={setRelated}
                        personOptions={household.peopleOf(PersonKind.CLIENT, PersonKind.FAMILY)}
                    />
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!canSave} // Each step's failure is toasted by its mutation
                        onClick={() => submit().catch(() => {})}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type AddRequest = { kind: PersonKind; onCreated: (personOrgGUID: string) => void };

/**
 * Add person / Add entity for a view: `addPerson(kind, onCreated)` opens the dialog for that kind, and `dialog` is
 * what the view renders for it. A new person's card is opened through `onCreated` by whoever asked.
 */
export const useAddPersonDialog = (
    householdGUID: string,
    household: OnboardingHousehold,
): { addPerson: (kind: PersonKind, onCreated?: (personOrgGUID: string) => void) => void; dialog: ReactNode } => {
    const [request, setRequest] = useState<AddRequest>();
    const close = () => setRequest(undefined);
    const addPerson = (kind: PersonKind, onCreated: (personOrgGUID: string) => void = () => {}) =>
        setRequest({ kind, onCreated });
    const dialog =
        request?.kind === PersonKind.ENTITY ? (
            <AddEntityDialog
                householdGUID={householdGUID}
                household={household}
                onClose={close}
                onCreated={request.onCreated}
            />
        ) : request ? (
            <AddPersonDialog
                householdGUID={householdGUID}
                tenantGUID={household.tenantGUID}
                initialKind={request.kind}
                onClose={close}
                onCreated={request.onCreated}
            />
        ) : null;
    return { addPerson, dialog };
};
